import { addHook, DateRange } from '@viveaesthetics/elemy-shared-core'
import { datadogRum } from '@datadog/browser-rum'
import { addDays, isBefore } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'

import { publicClient } from '@/graphql/client'
import {
  GetProvidersAvailableWindowsByDayQuery,
  useGetProvidersAvailableWindowsByDayQuery
} from '@/graphql/generated/schema'

const DEFAULT_MINIMUM_AVAILABILITY_WINDOW_MINUTES = 30
const DEFAULT_SLOT_DURATION_MINUTES = 60
const DEFAULT_SLOT_STEP_INCREMENT_MINUTES = 15

export interface ProviderTimeSlot {
  startTime: string
  endTime: string
  duration: string
}

export const transformTimeSlots = (
  data: GetProvidersAvailableWindowsByDayQuery
): Record<string, ProviderTimeSlot[]> => {
  const providersAvailableWindowsByDay: Record<string, ProviderTimeSlot[]> = {}

  if (
    data.providersAvailableWindowsByDay.__typename ===
    'ProvidersAvailableWindowsByDayFail'
  ) {
    datadogRum.addError(
      new Error(data.providersAvailableWindowsByDay.error.message)
    )
  }

  if (
    data.providersAvailableWindowsByDay.__typename ===
    'ProvidersAvailableWindowsByDaySuccess'
  ) {
    for (const providerAvailability of data.providersAvailableWindowsByDay
      .providerAvailableWindows) {
      const { providerId, providerAvailableWindowsByDay } = providerAvailability
      const timezone =
        providerAvailableWindowsByDay.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

      providerAvailableWindowsByDay.dailyTimeWindows.forEach(
        dailyTimeWindow => {
          const slots = []
          const date = dailyTimeWindow.day
          for (const availability of dailyTimeWindow.timeWindows) {
            const startDateTimeStr = `${date}T${availability.start}`
            const startDate = zonedTimeToUtc(startDateTimeStr, timezone)

            const endDateTimeStr = `${date}T${availability.end}`
            let endDate = zonedTimeToUtc(endDateTimeStr, timezone)
            if (isBefore(endDate, startDate)) {
              endDate = addDays(endDate, 1)
            }

            slots.push({
              startTime: startDate.toISOString(),
              endTime: endDate.toISOString(),
              duration: 'PT1H'
            })
          }

          if (slots.length > 0) {
            providersAvailableWindowsByDay[providerId] =
              providersAvailableWindowsByDay[providerId]
                ? [...providersAvailableWindowsByDay[providerId], ...slots]
                : [...slots]
          }
        }
      )
    }
  }

  return providersAvailableWindowsByDay
}

interface UseGetProvidersAvailableWindowsByDayProps {
  providerIds: string[]
  dateRange: DateRange<string>
  minimumAvailabilityWindowMinutes?: number
  enabled?: boolean
  // slot duration minutes and increment minutes shouldn't be used with multiple providers
  // cause it is very expensive operation and cause performance issues on BE
  slotDurationMinutes?: number
  slotStepIncrementMinutes?: number
}

export const useGetProvidersAvailableWindowByDay = addHook({
  useGetProvidersAvailableWindowByDay: ({
    providerIds,
    dateRange,
    minimumAvailabilityWindowMinutes = DEFAULT_MINIMUM_AVAILABILITY_WINDOW_MINUTES,
    slotDurationMinutes,
    slotStepIncrementMinutes,
    enabled = true
  }: UseGetProvidersAvailableWindowsByDayProps) => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

    const { data, fetchStatus, isLoading } =
      useGetProvidersAvailableWindowsByDayQuery(
        publicClient,
        {
          providerAvailableWindowsByDayInput: {
            providerIds,
            startDate: dateRange.start,
            endDate: dateRange.end,
            timezone: timeZone,
            minimumAvailabilityWindowMinutes,
            ...(slotDurationMinutes && { slotDurationMinutes }),
            ...(slotStepIncrementMinutes && { slotStepIncrementMinutes })
          }
        },
        {
          enabled,
          select: transformTimeSlots
        }
      )

    return {
      data,
      isLoading: isLoading && fetchStatus !== 'idle'
    }
  }
})

export const useGetProviderAvailableWindowByDay = addHook({
  useGetProviderAvailableWindowByDay: ({
    providerId,
    dateRange,
    withGranularSlots = false,
    enabled = true
  }: {
    providerId: string
    dateRange: DateRange<string>
    withGranularSlots?: boolean
    enabled?: boolean
  }) => {
    return useGetProvidersAvailableWindowByDay({
      providerIds: [providerId],
      dateRange,
      minimumAvailabilityWindowMinutes:
        DEFAULT_MINIMUM_AVAILABILITY_WINDOW_MINUTES,
      ...(withGranularSlots && {
        slotDurationMinutes: DEFAULT_SLOT_DURATION_MINUTES,
        slotStepIncrementMinutes: DEFAULT_SLOT_STEP_INCREMENT_MINUTES
      }),
      enabled
    })
  }
})
